import React, { useCallback } from "react";

interface SearchResult {
  id: string;
  name: string;
  type: "audio" | "video" | "image" | "vector";
  date: string;
  folderId?: string | null;
  folderName?: string;
  folderColor?: string;
  duration?: string;
}

interface LibrarySearchResultsProps {
  query: string;
  results: SearchResult[];
  onClose: () => void;
  onResultClick: (id: string) => void;
  onLocationClick?: (folderId: string | null) => void;
  loading?: boolean;
  ti18n?: Record<string, string>;
}

const t = (ti18n: Record<string, string> | undefined, key: string): string => {
  if (ti18n) {
    return window.getI18n(ti18n, key);
  }
  return key;
};

/* ------------------------------------------------------------------
   Style constants
   ------------------------------------------------------------------ */

const HEADER_STYLE: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "12px 0",
  borderBottom: "1px solid var(--border-default-color, #eee)",
};

const HEADER_TITLE_STYLE: React.CSSProperties = {
  fontSize: "var(--text-base, 14px)",
  fontWeight: 700,
  color: "var(--text-default-color, #222)",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  minWidth: 0,
};

const CLOSE_BUTTON_STYLE: React.CSSProperties = {
  width: 32,
  height: 32,
  background: "transparent",
  border: "none",
  padding: 0,
  margin: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
  flexShrink: 0,
};

const CLOSE_ICON_STYLE: React.CSSProperties = {
  fontSize: "var(--text-xl, 20px)",
  color: "var(--text-muted, #888)",
  WebkitTextStroke: "0.5px",
  lineHeight: 1,
  transition: "color 0.15s ease",
};

const COLUMN_HEADER_ROW_STYLE: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 12,
  padding: "8px 0",
  borderBottom: "1px solid var(--border-default-color, #eee)",
};

const COLUMN_HEADER_STYLE: React.CSSProperties = {
  fontSize: "var(--text-sm, 12px)",
  fontWeight: 700,
  color: "var(--text-muted, #888)",
  textTransform: "uppercase",
  letterSpacing: "0.5px",
  lineHeight: 1.4,
};

const RESULT_ROW_STYLE: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 12,
  padding: "10px 0",
  borderBottom: "1px solid rgba(var(--border-default-color-rgb, 0, 0, 0), 0.06)",
  cursor: "pointer",
  transition: "background 0.15s ease",
};

const TYPE_ICON_STYLE: React.CSSProperties = {
  fontSize: "var(--text-lg, 18px)",
  color: "var(--text-muted, #888)",
  WebkitTextStroke: "0.5px",
  lineHeight: 1,
  flexShrink: 0,
  width: 24,
  textAlign: "center",
};

const NAME_STYLE: React.CSSProperties = {
  flex: 1,
  minWidth: 0,
  fontWeight: 500,
  fontSize: "var(--text-base, 14px)",
  color: "var(--text-default-color, #222)",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

const DATE_STYLE: React.CSSProperties = {
  minWidth: 100,
  flexShrink: 0,
  fontSize: "var(--text-sm, 12px)",
  color: "var(--text-muted, #888)",
  whiteSpace: "nowrap",
};

const LOCATION_STYLE: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 6,
  minWidth: 120,
  flexShrink: 0,
};

const LOCATION_ICON_STYLE: React.CSSProperties = {
  fontSize: "var(--text-base, 14px)",
  WebkitTextStroke: "0.5px",
  lineHeight: 1,
  flexShrink: 0,
};

const LOCATION_NAME_STYLE: React.CSSProperties = {
  fontSize: "var(--text-sm, 12px)",
  color: "var(--text-muted, #888)",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

const LOCATION_BUTTON_STYLE: React.CSSProperties = {
  ...LOCATION_STYLE,
  background: "transparent",
  border: "none",
  padding: 0,
  margin: 0,
  cursor: "pointer",
  textAlign: "left",
  font: "inherit",
};

const EMPTY_STATE_STYLE: React.CSSProperties = {
  padding: "40px 0",
  textAlign: "center",
  fontSize: "var(--text-base, 14px)",
  color: "var(--text-muted, #888)",
};

/* ------------------------------------------------------------------
   Component
   ------------------------------------------------------------------ */

const LibrarySearchResults: React.FC<LibrarySearchResultsProps> = ({
  query,
  results,
  onClose,
  onResultClick,
  onLocationClick,
  loading = false,
  ti18n,
}) => {
  const handleRowMouseEnter = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      e.currentTarget.style.background = "var(--color-black-10, rgba(0,0,0,0.06))";
    },
    []
  );

  const handleRowMouseLeave = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      e.currentTarget.style.background = "transparent";
    },
    []
  );

  const handleCloseMouseEnter = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      const icon = e.currentTarget.querySelector("i");
      if (icon) {
        (icon as HTMLElement).style.color = "var(--text-default-color, #222)";
      }
    },
    []
  );

  const handleCloseMouseLeave = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      const icon = e.currentTarget.querySelector("i");
      if (icon) {
        (icon as HTMLElement).style.color = "var(--text-muted, #888)";
      }
    },
    []
  );

  const handleLocationClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>, folderId: string | null | undefined) => {
      e.stopPropagation();
      onLocationClick?.(folderId || null);
    },
    [onLocationClick]
  );

  const handleLocationKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLButtonElement>) => {
      e.stopPropagation();
    },
    []
  );

  return (
    <div style={{ backgroundColor: "var(--bg-default, #fff)" }}>
      {/* Header */}
      <div style={HEADER_STYLE}>
        <span style={HEADER_TITLE_STYLE}>
          {t(ti18n, "SEARCH_RESULTS_FOR")} &lsquo;{query}&rsquo;
        </span>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close search results"
          style={CLOSE_BUTTON_STYLE}
          onMouseEnter={handleCloseMouseEnter}
          onMouseLeave={handleCloseMouseLeave}
        >
          <i className="pe-7s-close" style={CLOSE_ICON_STYLE} />
        </button>
      </div>

      {/* Loading state */}
      {loading && (
        <div style={EMPTY_STATE_STYLE}>
          {t(ti18n, "SEARCHING")}
        </div>
      )}

      {/* Empty state */}
      {!loading && results.length === 0 && (
        <div style={EMPTY_STATE_STYLE}>
          {t(ti18n, "NO_RESULTS_FOUND")}
        </div>
      )}

      {/* Results table */}
      {!loading && results.length > 0 && (
        <>
          {/* Column headers */}
          <div style={COLUMN_HEADER_ROW_STYLE}>
            {/* Spacer for type icon column */}
            <div style={{ width: 24, flexShrink: 0 }} />
            <div style={{ ...COLUMN_HEADER_STYLE, flex: 1, minWidth: 0 }}>
              {t(ti18n, "NAME")}
            </div>
            <div style={{ ...COLUMN_HEADER_STYLE, minWidth: 100, flexShrink: 0 }}>
              {t(ti18n, "DATE")}
            </div>
            <div style={{ ...COLUMN_HEADER_STYLE, minWidth: 120, flexShrink: 0 }}>
              {t(ti18n, "LOCATION")}
            </div>
          </div>

          {/* Result rows */}
          {results.map((result) => (
            <div
              key={result.id}
              role="button"
              tabIndex={0}
              style={RESULT_ROW_STYLE}
              onClick={() => onResultClick(result.id)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  onResultClick(result.id);
                }
              }}
              onMouseEnter={handleRowMouseEnter}
              onMouseLeave={handleRowMouseLeave}
            >
              {/* Type icon */}
              <i
                className={
                  result.type === "audio"
                    ? "pe-7s-music"
                    : result.type === "video"
                      ? "pe-7s-video"
                      : result.type === "image"
                        ? "pe-7s-photo"
                        : "pe-7s-paint-bucket"
                }
                style={TYPE_ICON_STYLE}
              />

              {/* Name */}
              <span style={NAME_STYLE}>{result.name}</span>

              {/* Date */}
              <span style={DATE_STYLE}>{result.date}</span>

              {/* Location */}
              {onLocationClick ? (
                <button
                  type="button"
                  className="library-search-result-location"
                  style={LOCATION_BUTTON_STYLE}
                  onClick={(e) => handleLocationClick(e, result.folderId)}
                  onKeyDown={handleLocationKeyDown}
                >
                  <i
                    className="pe-7s-folder"
                    style={{
                      ...LOCATION_ICON_STYLE,
                      color: result.folderColor || "var(--text-muted, #888)",
                    }}
                  />
                  <span className="library-search-result-location-name" style={LOCATION_NAME_STYLE}>
                    {result.folderName || t(ti18n, "MY_LIBRARY")}
                  </span>
                </button>
              ) : (
                <span style={LOCATION_STYLE}>
                  <i
                    className="pe-7s-folder"
                    style={{
                      ...LOCATION_ICON_STYLE,
                      color: result.folderColor || "var(--text-muted, #888)",
                    }}
                  />
                  <span style={LOCATION_NAME_STYLE}>
                    {result.folderName || t(ti18n, "MY_LIBRARY")}
                  </span>
                </span>
              )}
            </div>
          ))}
        </>
      )}
    </div>
  );
};

export default LibrarySearchResults;
